import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil, combineLatest } from 'rxjs';

import { CategoryService } from '../../osm-services/category.service';
import { UserAppService } from '../../osm-services/user-app.service';
import { CategoryWithApps, AppInCategory } from '../../models/category.model';

@Component({
  selector: 'app-app-selector-sidebar',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './app-selector-sidebar.component.html',
  styleUrls: ['./app-selector-sidebar.component.scss']
})
export class AppSelectorSidebarComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  categories: CategoryWithApps[] = [];
  applications: AppInCategory[] = [];
  filteredApplications: AppInCategory[] = [];
  userApps: any[] = [];
  selectedCategoryName: string = '';
  isLoading = false;
  error: string = '';

  constructor(
    private categoryService: CategoryService,
    private userAppService: UserAppService
  ) {}

  ngOnInit() {
    this.loadData();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadData() {
    this.isLoading = true;
    this.error = '';

    // Load categories with apps and user apps in parallel
    combineLatest([
      this.categoryService.getCategoriesWithApps(),
      this.userAppService.getUserApps()
    ]).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: ([categoriesResponse, userAppsResponse]) => {
        this.categories = categoriesResponse.Items || [];
        this.userApps = userAppsResponse.items || [];
        
        // Extract all apps from categories
        this.applications = [];
        this.categories.forEach(category => {
          if (category.apps) {
            this.applications.push(...category.apps);
          }
        });
        
        this.filterApplications();
        this.isLoading = false;
      },
      error: (error) => {
        this.error = 'Failed to load categories and user apps';
        this.isLoading = false;
        console.error('Error loading data:', error);
      }
    });
  }

  onCategoryChange() {
    this.filterApplications();
  }

  private filterApplications() {
    if (!this.selectedCategoryName) {
      this.filteredApplications = [...this.applications];
    } else {
      this.filteredApplications = this.applications.filter(
        app => app.category === this.selectedCategoryName
      );
    }
  }

  isAppSelected(appId: string): boolean {
    return this.userApps.some(userApp => userApp.appId === appId);
  }

  onAppToggle(app: AppInCategory, event: any) {
    const isChecked = event.target.checked;
    
    if (isChecked) {
      this.addAppToUser(app);
    } else {
      this.removeAppFromUser(app);
    }
  }

  private addAppToUser(app: AppInCategory) {
    this.userAppService.addUserApp(app.appliId).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        // Update local user apps list
        this.userApps.push({
          appId: app.appliId,
          name: app.name,
          category: app.category,
          icon: app.icon,
          addedAt: new Date().toISOString()
        });
      },
      error: (error) => {
        this.error = 'Failed to add app to your list';
        console.error('Error adding app:', error);
        // Revert the checkbox if the API call failed
        setTimeout(() => {
          const checkbox = document.querySelector(`input[data-app-id="${app.appliId}"]`) as HTMLInputElement;
          if (checkbox) checkbox.checked = false;
        }, 0);
      }
    });
  }

  private removeAppFromUser(app: AppInCategory) {
    this.userAppService.removeUserApp(app.appliId).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        // Update local user apps list
        this.userApps = this.userApps.filter(userApp => userApp.appId !== app.appliId);
      },
      error: (error) => {
        this.error = 'Failed to remove app from your list';
        console.error('Error removing app:', error);
        // Revert the checkbox if the API call failed
        setTimeout(() => {
          const checkbox = document.querySelector(`input[data-app-id="${app.appliId}"]`) as HTMLInputElement;
          if (checkbox) checkbox.checked = true;
        }, 0);
      }
    });
  }

  clearError() {
    this.error = '';
  }

  onImageError(event: Event) {
    const target = event.target as HTMLImageElement;
    if (target) {
      target.style.display = 'none';
    }
  }
}
